import { Controller, useFormContext } from 'react-hook-form'
import { grey } from '../../../colors'
import { Box, Autocomplete, Chip, TextField, FormLabel } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import CancelIcon from '@mui/icons-material/Cancel'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface EmailAliasesInputProps {
  name: string
  label?: string
  placeholder?: string
  defaultValue?: string[]
  disabled?: boolean
  primaryEmail?: string
}

const EmailAliasesInput: React.FC<EmailAliasesInputProps> = ({
  name,
  label = 'Email Aliases',
  placeholder = 'Enter an email alias and press Enter',
  defaultValue,
  disabled = false,
  primaryEmail
}) => {
  const { control, setValue, getValues } = useFormContext()
  const theme = useTheme()
  const [inputText, setInputText] = useState<string>('')
  const [error, setError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const addAlias = (value: string): void => {
    const current: string[] = getValues(name) || []
    setValue(name, [...current, value], { shouldDirty: true })
  }

  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setValue(name, defaultValue, { shouldDirty: false })
    }
  }, [])

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field: { value = [], onChange, ref } }) => (
        <Box>
            <FormLabel required={false} sx={{ color: grey[700], fontSize: '16px' }}> {label} </FormLabel>
            <Autocomplete
              multiple
              freeSolo
              value={value}
              sx={{ mt: theme.spacing(2) }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setInputText(newInputValue)
                }
              }}
              clearIcon={<CancelIcon sx={{ color: grey[950] }} />}
              inputValue={inputText}
              onChange={(_, newValue) => {
                const normalized = Array.from(
                  new Set(
                    (newValue as string[])
                      .map(v => v.trim().toLowerCase())
                      .filter(v => {
                        const isValidEmail = emailRegex.test(v)
                        const isPrimary = primaryEmail ? v === primaryEmail.toLowerCase() : false
                        return isValidEmail && !isPrimary
                      })
                  )
                )
                onChange(normalized)
              }}
              options={[]}
              disabled={disabled}
              renderTags={(val, getTagProps) =>
                val.map((option, index) => (
                  <Chip
                    color='primary'
                    label={option}
                    {...getTagProps({ index })}
                    size='small'
                    key={index}
                    deleteIcon={
                      <CancelIcon sx={{ color: `${grey[400]} !important` }} />
                    }
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  placeholder={placeholder}
                  error={error}
                  helperText={error && errorMessage}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const value = inputText.trim().toLowerCase()
                      const current: string[] = getValues(name) || []
                      const isValid: boolean = emailRegex.test(value) && !current.includes(value) && value !== primaryEmail?.toLowerCase()
                      if (isValid) {
                        addAlias(value)
                        setInputText('')
                        setError(false)
                      } else {
                        setError(true)
                        setErrorMessage('Invalid or duplicate email address')
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = inputText.trim().toLowerCase()
                    const current: string[] = getValues(name) || []
                    const isValid: boolean = emailRegex.test(value) && !current.includes(value) && value !== primaryEmail?.toLowerCase()
                    if (isValid) {
                      addAlias(value)
                    }
                    setInputText('')
                  }}
                />
              )}
            />
        </Box>
      )}
    />
  )
}

export default EmailAliasesInput
